import React from "react";
import styled from "styled-components";
import { Alert, Button, Modal, Space, Spin } from "antd";
import { DownloadOutlined, EditOutlined, ExportOutlined } from "@ant-design/icons";

/**
 * A PDF, read in place.
 *
 * Every PDF here previously opened in a new browser tab, which throws the reader
 * out of the app to look at something the app is about. The same viewer serves a
 * template (unfilled, tokens showing) and a finished brochure, because from the
 * reader's side they are the same act.
 *
 * The browser's own PDF viewer does the work — it scrolls, zooms, prints, and
 * Ctrl/Cmd+F searches the text, which on a template means you can search it for
 * `{{` and find every token still in place.
 */

const Frame = styled.div`
  height: calc(96vh - 110px);
  background: #525659;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  .waiting {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: rgba(255, 255, 255, 0.75);
  }
`;

function PdfPreviewModal({
  title,
  url,
  loading = false,
  loadingHint,
  error,
  downloadUrl,
  downloadLabel = "Download",
  onEdit,
  onClose,
}: {
  title: string;
  url: string | null;
  loading?: boolean;
  loadingHint?: string;
  error?: string | null;
  downloadUrl?: string | null;
  downloadLabel?: string;
  /** A brochure: open its slides for editing, in place of the reader. */
  onEdit?: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      visible
      onCancel={onClose}
      title={title}
      width="90vw"
      style={{ top: 24 }}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
      footer={
        <Space>
          {onEdit && (
            <Button icon={<EditOutlined />} onClick={() => { onClose(); onEdit(); }}>
              Edit slides
            </Button>
          )}
          {downloadUrl && (
            <Button icon={<DownloadOutlined />} href={downloadUrl}>
              {downloadLabel}
            </Button>
          )}
          {url && (
            <Button
              icon={<ExportOutlined />}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Open in a tab
            </Button>
          )}
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Space>
      }
    >
      <Frame>
        {error ? (
          <div className="waiting">
            <Alert type="error" showIcon message={error} />
          </div>
        ) : loading || !url ? (
          <div className="waiting">
            <Spin />
            {loadingHint && <span>{loadingHint}</span>}
          </div>
        ) : (
          <iframe src={url} title={title} />
        )}
      </Frame>
    </Modal>
  );
}

export default PdfPreviewModal;
